// src/kontrolEdge/routes.tsx
import React from "react";
import SubscriptionsPage from "../settings/account/components/Subscriptions/SubscriptionsPage";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import {
    getExtenderPopCarrierAccessAllowedTenantTypes,
    getMaintenanceWindowsAllowedTenantTypes,
    getSubscriptionsAllowedTenantTypes,
} from "@shared/navigation/SideMenu/sideMenuVisibility";
import {
    isCarrierAccessEnabled,
    isCarrierBridgeEnabled,
    isExtenderPopEnabled,
    isInternetMonitoringEnabled,
    isSelfServiceOrderEnabled,
    isSubscriptionsManagerEnabled,
} from "@shared/config/featureFlags";
import { Route, Navigate } from "react-router-dom";
import { RedirectWithSearch } from "@shared/components/RedirectWithSearch";
import PortCreationFlow from "./views/PortCreationFlow";
import MainPort from "./components/Port/MainPort";
// import ServicesOverview from "./views/KontrolEdgeMain";
import MainInternet from "./components/Internet/MainInternet";
import MainBackbone from "./components/Backbone/MainBackbone";
import BackboneDetails from "./components/Backbone/BackboneDetails/BackboneDetails";
import MainCloudConnectivity from "./components/CloudConnectivity/MainCloudConnectivity";
import MainDataCenter from "./components/DataCenter/MainDataCenter";
import PortDetails from "./components/Port/PortDetails/PortDetails";
import HardwareInventoryPage from "./components/ResourceManager/Hardware/HardwareInventoryPage";
import PortInventoryPage from "./components/ResourceManager/Hardware/Port/PortInventoryPage";
import LastMileInventoryPage from "./components/ResourceManager/LastMile/LastMileInventoryPage";
import InternetCreationFlow from "./views/InternetCreationFlow";
import IpInventoryPage from "./components/ResourceManager/IpBlock/IpInventoryPage";
import SubnetInventoryPage from "./components/ResourceManager/PublicSubnet/SubnetInventoryPage";
import InternetDetails from "./components/Internet/InternetDetails/InternetDetails";
import ManagerOverview from "./views/ManagerOverview";
import DatacenterDetails from "./components/DataCenter/DataCenterDetails/DatacenterDetails";
import OutagesView from "./views/Outages/OutagesView";
import CloudConnectivityDetails from "./components/CloudConnectivity/CloudDetails.tsx/CloudConnectivityDetails";
import ThirdPartyInventoryPage from "./components/ResourceManager/ThirdParty/ThirdPartyInventoryPage";
import PartnerPage from "./components/ResourceManager/Tenants/Partner/PartnerPage";
import CustomerPage from "./components/ResourceManager/Tenants/Customer/CustomerPage";
import NotificationsPage from "settings/account/components/Notifications/NotificationsPage";
import NotificationsHistory from "settings/account/components/Notifications/NotificationsHistory/NotificationsHistory";
import FwaasPage from "@shared/components/fwaas/FwaasPage";
import SdwanHubPage from "@shared/components/sdwanHub/SdwanHubPage";
import MainExtenderPop from "./components/ExtenderPop/MainExtenderPop";
import ExtenderPopDetails from "./components/ExtenderPop/ExtenderPopDetails";
import ExtenderPopCpeDetails from "./components/ExtenderPop/ExtenderPopCpeDetails";
import ExtenderPopCarrierAccessAdminPage from "./components/ExtenderPop/CarrierAccess/ExtenderPopCarrierAccessAdminPage";
import CarrierAccessDetails from "./components/CarrierAccess/CarrierAccessDetails/CarrierAccessDetails";
import CarrierBridgeDetails from "./components/CarrierBridge/CarrierBridgeDetails/CarrierBridgeDetails";
import MainCarrierBridge from "./components/CarrierBridge/MainCarrierBridge";
import SdwanHubDetails from "./components/Sdwan/HubDetails/SdwanHubDetails";
import FwaasDetails from "./components/Fwaas/FwaasDetails/FwaasDetails";
import SdwanBranchDetails from "./components/Sdwan/BranchDetails/SdwanBranchDetails";
import QuotationsPage from "@shared/components/quotations/QuotationsPage";

import DCConnectionsInventory from "./components/ResourceManager/DataCenter/DatacenterPage";
import DatacenterPage from "./components/ResourceManager/Kontroledge/DataCenter/DatacenterPage";
import InternetInventoryPage from "./components/ResourceManager/Kontroledge/Internet/InternetPage";
import CloudConnectivityPage from "./components/ResourceManager/Kontroledge/CloudConnectivity/CloudConnectivityPage";
import FwaasRMPage from "./components/ResourceManager/Kontroledge/Fwaas/FwaasPage";
import CarrierBridgeRMPage from "./components/ResourceManager/Kontroledge/CarrierBridge/CarrierBridgePage";
import CarriersRMPage from "./components/ResourceManager/Kontroledge/Carriers/CarriersPage";
import InternetRMPage from "./components/ResourceManager/Kontroledge/Internet/InternetPage";
import PortRMPage from "./components/ResourceManager/Kontroledge/Port/PortKEPage";
import PresalesDesignerPage from "./components/PresalesDesigner/PresalesDesignerPage";
import AIQuotationPage from "./components/AIQuotation/AIQuotationPage";
import PermissionRoute from "@shared/components/PermissionRoute";
import DatacenterConnectionsPage from "./components/ResourceManager/DatacenterConnections/DatacenterConnectionsPage";
import TopologyPage from "./components/ResourceManager/Topology/TopologyPage";
import ReportsPage from "settings/account/components/Reports/ReportsPage";
import FortiFlexPage from "./components/FortiFlex/FortiFlexPage";
import MaintenanceWindowsPage from "./components/ResourceManager/MaintenanceWindows/MaintenanceWindowsPage";
import NetworkAlertsPage from "./components/NetworkAlerts/NetworkAlertsPage";
import NetworkMonitoringPage from "./components/NetworkMonitoring/NetworkMonitoringPage";
import { KontrolAlertsRoute } from "@shared/components/KontrolAlertsRoute";
import RequestPage from "../settings/support/RequestPage";



const subscriptionsRoutes = isSubscriptionsManagerEnabled()
    ? [
          <Route
              key="adminCenter-subscriptions"
              path="kontrol/admin-center/subscriptions"
              element={
                  <RequireTenantType
                      allowedTypes={getSubscriptionsAllowedTenantTypes()}
                  >
                      <SubscriptionsPage />
                  </RequireTenantType>
              }
          />,
      ]
    : [];

// Extended PoP is BETA: hidden in production via isExtenderPopEnabled(), so
// both routes are absent in prod even by direct URL. The sidemenu row and the
// Tags Manager wizard entry share the same flag.
const extenderPopRoutes = isExtenderPopEnabled()
    ? [
          <Route
              key="extenderPop"
              path="/kontrol/extender-pop"
              element={<MainExtenderPop />}
          />,
          <Route
              key="extenderPopDetails"
              path="/kontrol/extender-pop-details"
              element={<ExtenderPopDetails />}
          />,
          <Route
              key="extenderPopCpeDetails"
              path="/kontrol/extender-pop-cpe-details"
              element={<ExtenderPopCpeDetails />}
          />,
          // Admin Center: the site's Carrier Access, managed by a Global
          // Admin (CTX-830). Same tenant-type rule as its menu entry.
          <Route
              key="adminCenter-extenderPopCarrierAccess"
              path="kontrol/admin-center/extended-pop-carrier-access"
              element={
                  <RequireTenantType
                      allowedTypes={getExtenderPopCarrierAccessAllowedTenantTypes()}
                  >
                      <ExtenderPopCarrierAccessAdminPage />
                  </RequireTenantType>
              }
          />,
      ]
    : [];

// The deprecated Add Port / Add Internet flows are hidden in production
// (isSelfServiceOrderEnabled): a saved link lands on the service list.
const selfServiceOrderRoutes = isSelfServiceOrderEnabled()
    ? [
          <Route key="addPort" path="/kontrol/add-port" element={<PortCreationFlow />} />,
          <Route key="addInternet" path="/kontrol/add-ipx" element={<InternetCreationFlow />} />,
      ]
    : [
          <Route key="addPort" path="/kontrol/add-port" element={<Navigate to="/kontrol/port" replace />} />,
          <Route key="addInternet" path="/kontrol/add-ipx" element={<Navigate to="/kontrol/ipx" replace />} />,
      ];

// Carrier Access ("Backhaul" in the UI) is GA since CTX-1089;
// isCarrierAccessEnabled() stays as the switch. It has no list of its own:
// the CPE list and detail and the ports link each 84- to this detail. The
// URL went out under its UI name; the old one keeps the links saved on
// staging working.
const carrierAccessRoutes = isCarrierAccessEnabled()
    ? [
          <Route
              key="carrierAccessDetails"
              path="/kontrol/backhaul-details"
              element={<CarrierAccessDetails />}
          />,
          <Route
              key="carrierAccessDetailsLegacy"
              path="/kontrol/carrier-access-details"
              element={<RedirectWithSearch to="/kontrol/backhaul-details" />}
          />,
      ]
    : [];

// Carrier Bridge is BETA until its commercial name and SKU are final
// (CTX-955): hidden in production via isCarrierBridgeEnabled(), so the
// customer views and the Admin Center screens are absent in prod even by
// direct URL. The sidemenu row and the Admin Center sections share the flag.
const carrierBridgeRoutes = isCarrierBridgeEnabled()
    ? [
          <Route key="carrierBridge" path="/kontrol/carrier-bridge" element={<MainCarrierBridge />} />,
          <Route
              key="carrierBridgeDetails"
              path="/kontrol/carrier-bridge-details"
              element={<CarrierBridgeDetails />}
          />,
          <Route
              key="adminCenter-carrier-bridge"
              path="kontrol/admin-center/carrier-bridge"
              element={<CarrierBridgeRMPage />}
          />,
          <Route
              key="adminCenter-carriers"
              path="kontrol/admin-center/carriers"
              element={<CarriersRMPage />}
          />,
      ]
    : [];

// Network monitoring (formerly Internet monitoring) is switchable through
// isInternetMonitoringEnabled() and carries the Alerts audience gate, matching
// the sidemenu row (see filterKontrolEventManagementItems). The old URL
// redirects so saved links and the changelog walkthrough keep landing.
const internetMonitoringRoutes = isInternetMonitoringEnabled()
    ? [
          <Route
              key="networkMonitoring"
              path="/kontrol/network-monitoring"
              element={
                  <KontrolAlertsRoute>
                      <NetworkMonitoringPage />
                  </KontrolAlertsRoute>
              }
          />,
          <Route
              key="internetIpMonitoringLegacy"
              path="/kontrol/internet-ip-monitoring"
              element={<Navigate to="/kontrol/network-monitoring" replace />}
          />,
      ]
    : [];

export const kontrolEdgeRoutes = [
    // <Route key="kontrolMain" path="/kontrol" element={<ServicesOverview />} />,
    <Route key="mainDashboard" path="/kontrol" element={<ManagerOverview />} />,
    <Route key="outagesDashboard" path="/kontrol/outages" element={<OutagesView />} />,
    ...selfServiceOrderRoutes,
    <Route key="mainPort" path="/kontrol/port" element={<MainPort />} />,
    <Route key="portDetails" path="/kontrol/port-details" element={<PortDetails />} />,
    <Route key="mainInternet" path="/kontrol/ipx" element={<MainInternet />} />,
    <Route key="internetDetails" path="/kontrol/internet/internet-details" element={<InternetDetails />} />,
    <Route key="mainDataCenter" path="/kontrol/datacenter" element={<MainDataCenter />} />,
    <Route key="mainDataCenter" path="/kontrol/datacenter-details" element={<DatacenterDetails />} />,
    <Route key="mainCloud" path="/kontrol/cloud-connectivity" element={<MainCloudConnectivity />} />,
    <Route key="cloudDetails" path="/kontrol/cloud-connectivity/details" element={<CloudConnectivityDetails />} />,
    <Route key="mainBackbone" path="/kontrol/backbone" element={<MainBackbone />} />,
    <Route key="backboneDetails" path="/kontrol/backbone-details" element={<BackboneDetails />} />,
    <Route key="fwaas" path="/kontrol/fwaas" element={<FwaasPage />} />,
    <Route key="fwaasDetails" path="/kontrol/fwaas-details" element={<FwaasDetails />} />,
    <Route key="sdwan-hub" path="/kontrol/sdwan-hub" element={<SdwanHubPage />} />,
  <Route key="sdwanHubDetails" path="/kontrol/sdwan-hub-details" element={<SdwanHubDetails />} />,
  <Route key="sdwanBranchDetails" path="/kontrol/sdwan-branch-details" element={<SdwanBranchDetails />} />,
    <Route key="reports" path="/kontrol/reports" element={<ReportsPage />} />,
    <Route
        key="networkAlerts"
        path="/kontrol/alerts"
        element={
            <KontrolAlertsRoute>
                <NetworkAlertsPage />
            </KontrolAlertsRoute>
        }
    />,
    <Route key="quotations" path="/kontrol/quotations" element={<QuotationsPage />} />,
    //Resource Manager
    <Route
        key="datacenter-inventory"
        path="kontrol/admin-center/datacenter-inventory" //DC Inventory **
        element={<DCConnectionsInventory />}
    />,
    <Route
        key="hardware-inventory"
        path="kontrol/admin-center/hardware"
        element={<HardwareInventoryPage />}
    />,
    <Route
        key="port-inventory"
        path="kontrol/admin-center/hardware/port"
        element={<PortInventoryPage />}
    />,
    <Route
        key="datacenter-connections"
        path="kontrol/admin-center/datacenter-connections"
        element={<DatacenterConnectionsPage />}
    />,
    <Route
        key="internet-inventory"
        path="/kontrol/admin-center/dci-internet"
        element={<InternetInventoryPage />}
    />,
    <Route
        key="lastMile-inventory"
        path="kontrol/admin-center/lastMile"
        element={<LastMileInventoryPage />}
    />,
    <Route
        key="ipblock-inventory"
        path="kontrol/admin-center/ip-block"
        element={<IpInventoryPage />}
    />,
    <Route
        key="publicsubnet-inventory"
        path="kontrol/admin-center/public-subnet"
        element={<SubnetInventoryPage />}
    />,
    <Route
        key="thirdparty-inventory"
        path="kontrol/admin-center/third-party"
        element={<ThirdPartyInventoryPage />}
    />,

    //Resource Manager Tenant
    <Route
        key="customers"
        path="kontrol/admin-center/customers"
        element={<CustomerPage />}
    />,
    <Route
        key="partners"
        path="kontrol/admin-center/partners"
        element={<PartnerPage />}
    />,
    //Resource Manager Kontrol Edge
    <Route
        key="adminCenter-port"
        path="kontrol/admin-center/port"
        element={<PortRMPage />}
    />,
    <Route
        key="adminCenter-internet"
        path="kontrol/admin-center/internet"
        element={<InternetRMPage />}
    />,
    <Route
        key="adminCenter-cloud"
        path="kontrol/admin-center/cloud"
        element={<CloudConnectivityPage />}
    />,
    <Route
        key="adminCenter-dci"
        path="kontrol/admin-center/data-center"
        element={<DatacenterPage />}
    />,
    <Route
        key="adminCenter-fwaas"
        path="kontrol/admin-center/fwaas"
        element={<FwaasRMPage />}
    />,

    <Route
        key="ke-topology"
        path="kontrol/admin-center/ke-topology"
        element={<TopologyPage />}
    />,

    <Route
        key="srv6-insight"
        path="kontrol/admin-center/srv6-insight"
        element={<Navigate to="/kontrol/admin-center/ke-topology?tab=traffic" replace />}
    />,

    <Route key="notifications" path="/kontrol/notifications-management" element={<NotificationsPage />} />,
    <Route key="notifications" path="/kontrol/notifications-history" element={<NotificationsHistory/>} />,
    
    // Presales Designer (Resource Manager / Sales Tools) - Requires Presales.Designer permission
    <Route 
        key="presales-designer" 
        path="/kontrol/admin-center/presales-designer" 
        element={
            <PermissionRoute requiredPermission="Presales.Designer">
                <PresalesDesignerPage />
            </PermissionRoute>
        } 
    />,
    <Route 
        key="presales-designer-detail" 
        path="/kontrol/admin-center/presales-designer/:orderId" 
        element={
            <PermissionRoute requiredPermission="Presales.Designer">
                <PresalesDesignerPage />
            </PermissionRoute>
        } 
    />,
    
    // AI Quotation Assistant (Sales Tools) - Requires Presales.Chat permission
    <Route 
        key="ai-quotation" 
        path="/kontrol/admin-center/ai-quotation" 
        element={
            <PermissionRoute requiredPermission="Presales.Chat">
                <AIQuotationPage />
            </PermissionRoute>
        } 
    />,

    // FortiFlex Report
    <Route
        key="fortiflex"
        path="kontrol/admin-center/fortiflex"
        element={<FortiFlexPage />}
    />,
    // Scheduled maintenance windows (CTX-1294). Same tenant-type allow-list as
    // its menu entry (`filterMaintenanceWindowsAdminSection`), so a direct URL
    // cannot reach what the menu hides.
    <Route
        key="adminCenter-maintenanceWindows"
        path="kontrol/admin-center/maintenance-windows"
        element={
            <RequireTenantType
                allowedTypes={getMaintenanceWindowsAllowedTenantTypes()}
            >
                <MaintenanceWindowsPage />
            </RequireTenantType>
        }
    />,
    // Subscriptions management (CTX-26). Registered only when the WIP flag is
    // on, and guarded by the same tenant-type allow-list the Admin Center menu
    // uses (`filterSubscriptionsAdminSection`), so a direct URL cannot reach
    // what the menu hides.
    // Technical Support under the product's prefix, so a link to a Kontrol
    // Edge ticket (`/kontrol/support/request?ticket=KS-…`, the participant
    // emails of CTX-1344) opens in Kontrol Edge whatever product the user
    // had last; `/support/request` keeps the product in session.
    <Route
        key="kontrol-support-request"
        path="/kontrol/support/request"
        element={<RequestPage />}
    />,
    ...extenderPopRoutes,
    ...carrierAccessRoutes,
    ...carrierBridgeRoutes,
    ...internetMonitoringRoutes,
    ...subscriptionsRoutes,
];
